import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Badge } from '@mantine/core';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { Segment } from '../../../../services/analytics-services/segment';
import { SUPPLIES_CARD_CLICKED, SUPPLIES_CHECKOUT_OPENED, SUPPLIES_CHECKOUT_POPUP_BLOCKED } from '../../../../constants/analytics-events';
import {
  SUPPLIES_DISPLAY_LOCATION,
  type SuppliesDisplayLocation,
} from '../../../home-page/version2/supplies-cards/supplies-page-constants';
import { createCheckout } from '../../api/create-checkout';
import { openCheckoutPopup } from '../../api/open-checkout-popup';
import { stripShopifyIdPrefix } from '../../api/shopify-id-utils';
import { messages } from '../../messages';
import style from './product-card.module.less';

type ProductCardProps = {
  productId: string;
  productName: string;
  price: number;
  compareAtPrice?: number;
  imageUrl: string;
  imageAlt: string;
  variantId: string;
  page?: SuppliesDisplayLocation;
};

export const ProductCard = ({
  productId,
  productName,
  price,
  compareAtPrice,
  imageUrl,
  imageAlt,
  variantId,
  page = SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE,
}: ProductCardProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();

  const discountPercentage = compareAtPrice ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : null;
  const hasDiscount = discountPercentage !== null && discountPercentage > 0;

  const handleBuyClick = useCallback(async () => {
    Segment.trackEvent(SUPPLIES_CARD_CLICKED, {
      page,
      productId: stripShopifyIdPrefix(productId),
    });

    setIsLoading(true);
    try {
      const { checkoutId, checkoutUrl } = await createCheckout(variantId);

      if (!checkoutUrl) {
        NotificationService.showNotification({
          title: intl.formatMessage(messages.checkoutError),
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
        return;
      }

      const popupWindow = openCheckoutPopup(checkoutUrl);
      if (!popupWindow) {
        Segment.trackEvent(SUPPLIES_CHECKOUT_POPUP_BLOCKED, {
          page,
          productId: stripShopifyIdPrefix(productId),
          checkoutId,
        });
        NotificationService.showNotification({
          title: intl.formatMessage(messages.popupBlockedError),
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
        return;
      }

      Segment.trackEvent(SUPPLIES_CHECKOUT_OPENED, {
        page,
        productId: stripShopifyIdPrefix(productId),
        checkoutId,
      });
    } catch {
      NotificationService.showNotification({
        title: intl.formatMessage(messages.checkoutError),
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  }, [productId, variantId, intl, page]);

  return (
    <div className={style.productCard}>
      <FlexColumn className={style.cardContent} gap={Gap.Space12}>
        <img alt={imageAlt} className={style.productImage} src={imageUrl} />
        <Text className={style.productName} textType={TextType.H4} value={productName} />
        <Badge
          className={hasDiscount ? style.discountBadge : style.discountBadgePlaceholder}
          color='active'
          fw={600}
          size='sm'
          variant='outline'
        >
          {hasDiscount ? intl.formatMessage(messages.discountBadge, { percent: discountPercentage }) : ' '}
        </Badge>
        <FlexRow className={style.buttonContainer}>
          <Button className={style.ctaButton} isLoading={isLoading} onClick={handleBuyClick} theme={THEME_CONSTANTS.secondary}>
            {hasDiscount ? (
              <>
                <span className={style.originalPriceButton}>${compareAtPrice?.toFixed(2)}</span>{' '}
                <span className={style.salePriceButton}>${price.toFixed(2)}</span>
              </>
            ) : (
              `$${price.toFixed(2)}`
            )}
          </Button>
        </FlexRow>
      </FlexColumn>
    </div>
  );
};
